import { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, Loader2, KeyRound, RotateCcw } from 'lucide-react';
import { apiFetch } from '../../stores/authStore';
import { ConfirmModal } from '../../components/atoms/Modal';

interface OrgCredential {
  id: string;
  label: string;
  accessKey: string;
  venue: string;
  environment: 'prod' | 'demo';
  createdAt: string;
}

export function OrgKeysPanel({ orgQueryParam = '' }: { orgQueryParam?: string }) {
  const [creds, setCreds] = useState<OrgCredential[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [label, setLabel] = useState('');
  const [accessKey, setAccessKey] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<OrgCredential | null>(null);
  const [replaceTarget, setReplaceTarget] = useState<OrgCredential | null>(null);

  const fetchCreds = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/manager/credentials${orgQueryParam}`);
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? 'Failed to load org keys');
        setCreds([]);
      } else {
        setCreds(data.credentials ?? []);
      }
    } catch {
      setError('Network error');
    }
    setLoading(false);
  }, [orgQueryParam]);

  useEffect(() => { fetchCreds(); }, [fetchCreds]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim() || !accessKey.trim() || !privateKey.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/manager/credentials${orgQueryParam}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          label: label.trim(),
          accessKey: accessKey.trim(),
          privateKeyPem: privateKey,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? 'Failed to add key');
      } else {
        setCreds((prev) => [...prev, data.credential]);
        setLabel('');
        setAccessKey('');
        setPrivateKey('');
        setShowForm(false);
      }
    } catch {
      setError('Network error');
    }
    setSubmitting(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    setDeleteTarget(null);
    const res = await apiFetch(`/api/manager/credentials/${id}${orgQueryParam}`, {
      method: 'DELETE',
    });
    if (res.ok) {
      setCreds((prev) => prev.filter((c) => c.id !== id));
    } else {
      const data = await res.json().catch(() => ({}));
      setError(data.error ?? 'Failed to delete key');
    }
  };

  const handleReplace = async () => {
    if (!replaceTarget) return;
    const id = replaceTarget.id;
    setReplaceTarget(null);
    setError(null);
    const res = await apiFetch(`/api/manager/credentials/${id}${orgQueryParam}`, {
      method: 'DELETE',
    });
    if (res.ok) {
      setCreds((prev) => prev.filter((c) => c.id !== id));
      setShowForm(true);
    } else {
      const data = await res.json().catch(() => ({}));
      setError(data.error ?? 'Failed to remove existing key');
    }
  };

  const hasKey = creds.length > 0;

  return (
    <section className="bg-card border-border mb-6 rounded-lg border p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <KeyRound size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-semibold">Org Key</h3>
          <span className="text-muted-foreground text-[11px]">
            ({hasKey ? '1 active' : 'none'})
          </span>
        </div>
        {hasKey ? (
          <button onClick={() => setReplaceTarget(creds[0])}
            className="flex items-center gap-1.5 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-500">
            <RotateCcw size={14} /> Replace Key
          </button>
        ) : (
          <button onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500">
            <Plus size={14} /> {showForm ? 'Cancel' : 'Add Key'}
          </button>
        )}
      </div>

      <p className="text-muted-foreground mb-3 text-[11px]">
        One Kalshi key per org. Used by every member whose <em>Use org keys</em> toggle is on.
        Private keys are encrypted at rest and never shown to the browser again after upload.
      </p>

      {error && (
        <div className="mb-3 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">{error}</div>
      )}

      {showForm && !hasKey && (
        <form onSubmit={handleCreate} className="bg-muted/50 border-border mb-3 space-y-2 rounded-md border p-3">
          <div>
            <label className="text-muted-foreground mb-1 block text-[11px]">Label</label>
            <input value={label} onChange={(e) => setLabel(e.target.value)}
              className="bg-card border-border text-foreground w-full rounded-md border px-2 py-1 text-xs focus:border-blue-500 focus:outline-none"
              placeholder="e.g. main-key-1" />
          </div>
          <div>
            <label className="text-muted-foreground mb-1 block text-[11px]">Access Key ID</label>
            <input value={accessKey} onChange={(e) => setAccessKey(e.target.value)}
              className="bg-card border-border text-foreground w-full rounded-md border px-2 py-1 font-mono text-xs focus:border-blue-500 focus:outline-none"
              placeholder="kalshi access key id" />
          </div>
          <div>
            <label className="text-muted-foreground mb-1 block text-[11px]">Private Key (PEM)</label>
            <textarea value={privateKey} onChange={(e) => setPrivateKey(e.target.value)}
              className="bg-card border-border text-foreground h-32 w-full rounded-md border px-2 py-1 font-mono text-[10px] focus:border-blue-500 focus:outline-none"
              placeholder="-----BEGIN RSA PRIVATE KEY-----..." />
          </div>
          <button type="submit" disabled={submitting}
            className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50">
            {submitting && <Loader2 size={12} className="animate-spin" />} Save Key
          </button>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
        </div>
      ) : creds.length === 0 ? (
        <p className="text-muted-foreground py-2 text-center text-xs">
          No org keys yet. Add one to enable org-keyed trading for this org.
        </p>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-muted-foreground border-border border-b">
              <th className="py-1.5 text-left font-medium">Label</th>
              <th className="py-1.5 text-left font-medium">Access Key</th>
              <th className="py-1.5 text-left font-medium">Env</th>
              <th className="py-1.5 text-left font-medium">Added</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {creds.map((c) => (
              <tr key={c.id} className="border-border/40 border-b last:border-0">
                <td className="py-1.5">{c.label}</td>
                <td className="py-1.5 font-mono text-[11px]">{c.accessKey.slice(0, 10)}…</td>
                <td className="py-1.5">{c.environment}</td>
                <td className="py-1.5 text-[11px]">{new Date(c.createdAt).toLocaleDateString()}</td>
                <td className="py-1.5 text-right">
                  <button onClick={() => setDeleteTarget(c)} title="Delete"
                    className="text-muted-foreground rounded p-1 hover:bg-red-500/10 hover:text-red-400">
                    <Trash2 size={12} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Org Key"
        message={`Remove "${deleteTarget?.label}"? Any user currently routing via org keys will fall back to personal keys (or fail if they have none).`}
        confirmLabel="Delete"
        variant="danger"
      />

      <ConfirmModal
        isOpen={!!replaceTarget}
        onClose={() => setReplaceTarget(null)}
        onConfirm={handleReplace}
        title="Replace Org Key"
        message={`This will delete "${replaceTarget?.label}" and let you upload a replacement. Any user currently routing through it will fail until the new key is in place.`}
        confirmLabel="Delete current & add new"
        variant="danger"
      />
    </section>
  );
}
